<template>
  <v-flex xs12>
    <v-tooltip bottom>
      <v-btn icon small :color="themes.client.light.primaryActionButton" slot="activator">
        <v-icon small>mdi-email</v-icon>
      </v-btn>
      <span> Email: {{ profile.email }} </span>
    </v-tooltip>
    <MobileNumberValidator v-if="profile.mobileNumber" :value="profile.mobileNumber">
      <v-tooltip bottom slot-scope="{ validator }">
        <v-btn slot="activator" :color="themes.client.light.primaryActionButton" small icon>
          <v-icon small>mdi-phone</v-icon>
        </v-btn>
        <div>Mobile no: {{ validator.mobileNumber }}</div>
      </v-tooltip>
    </MobileNumberValidator>
    <v-tooltip bottom>
      <v-menu offset-y offset-x max-width="50%" slot="activator">
        <v-btn slot="activator" :color="themes.client.light.primaryActionButton" small icon>
          <v-icon small>mdi-information-variant</v-icon>
        </v-btn>
        <v-card>
          <v-list>
            <!-- ID No & Birth Date -->
            <v-list-tile @click="" v-if="profile.identification">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <span
                    slot="activator"
                    v-if="profile.countryOfBirth"
                    :class="`flag-icon flag-icon-${profile.countryOfBirth.toLowerCase()}`"
                  />
                  <span>Nationality: {{ countryName(profile.countryOfBirth) }}</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-list-tile-title class="text-xs-left">
                  <span class="ml-3 ">{{ profile.identification }}</span>
                </v-list-tile-title>
                <v-list-tile-sub-title
                  class="ml-3 caption text-xs-left"
                  v-if="missing('IdentityDocument')"
                >
                  <v-icon small left :class="themes.client.light.warningText"
                    >mdi-alert-circle</v-icon
                  >
                  <span :class="themes.client.light.warningText">Identity Document Required</span>
                </v-list-tile-sub-title>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Birth Date -->
            <v-list-tile @click="" v-if="profile.bornAt">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-cake-variant</v-icon>
                  <span>Date of Birth</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <v-tooltip bottom>
                  <v-list-tile-content slot="activator">
                    <span class="ml-3 ">{{ new Date(profile.bornAt) | date('d MMMM yyy') }}</span>
                  </v-list-tile-content>
                  <span>
                    Birthday on {{ new Date(profile.bornAt) | date('d MMMM YYY') }} ({{
                      new Date(profile.bornAt) | inWords({ addSuffix: true })
                    }})
                  </span>
                </v-tooltip>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Gender -->
            <v-list-tile @click="" v-if="profile.gender">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-human-male-female</v-icon>
                  <span>Gender Identification</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-tooltip bottom>
                <v-list-tile-content slot="activator">
                  <span class=" ml-3">
                    {{ profile.gender }}
                  </span>
                </v-list-tile-content>
                <span class=""> Identifies as {{ profile.gender }} </span>
              </v-tooltip>
            </v-list-tile>

            <!-- Ethnicity -->
            <v-list-tile @click="" v-if="profile.ethnicity">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-earth</v-icon>
                  <span>Ethnicity</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-tooltip bottom>
                <v-list-tile-content slot="activator">
                  <span class=" ml-3">
                    {{ profile.ethnicity }}
                  </span>
                </v-list-tile-content>
                <span class=""> Ethnicity is {{ profile.ethnicity }} </span>
              </v-tooltip>
            </v-list-tile>

            <!-- Disability -->
            <v-list-tile @click="" v-if="profile.disabled">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-wheelchair-accessibility</v-icon>
                  <span>Limiting disability</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <span class="ml-3" v-if="profile.disabilityCategory">
                  {{ categories[profile.disabilityCategory] }}
                </span>
                <span class="ml-3" v-else>Yes</span>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Language Skills -->
            <v-list-tile @click="" v-if="showLanguages">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-account-voice</v-icon>
                  <span>Language Skills</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content class="ml-3">
                <v-layout align-center justify-space-between row fill-height>
                  <v-flex v-for="(language, i) in profile.languages" :key="`language-${i}`">
                    <span v-if="i > 0"> ,</span>
                    <v-tooltip bottom>
                      <span slot="activator">{{ language.language }}</span>
                      <span> {{ language.description }} </span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Work Rights -->
            <v-list-tile @click="" v-if="showWorkRights">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-briefcase</v-icon>
                  <span>Work Rights</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content class="ml-3">
                <v-layout align-center justify-space-between row fill-height>
                  <v-flex v-for="(right, i) in profile.workRights" :key="`work-right-${i}`">
                    <v-tooltip bottom>
                      <span
                        slot="activator"
                        v-if="right.countryCode"
                        :class="`flag-icon flag-icon-${right.countryCode.toLowerCase()}`"
                      />
                      <span> {{ right.country }} </span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Driving License -->
            <v-list-tile @click="" v-if="profile.driversLicense">
              <v-list-tile-avatar>
                <v-icon>mdi-card-account-details</v-icon>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <span class=" ml-3">
                  Valid driver's license
                </span>
              </v-list-tile-content>
            </v-list-tile>

            <!-- Current Location -->
            <v-list-tile @click="" v-if="profile.location">
              <v-list-tile-avatar>
                <v-tooltip bottom>
                  <v-icon slot="activator">mdi-map-marker</v-icon>
                  <span>Current Location</span>
                </v-tooltip>
              </v-list-tile-avatar>
              <v-list-tile-content>
                <span class=" ml-3">
                  {{ profile.location.address }}
                </span>
              </v-list-tile-content>
            </v-list-tile>
          </v-list>
        </v-card>
      </v-menu>
      <span> View Personal Information </span>
    </v-tooltip>
  </v-flex>
</template>

<script>
// Components
import MobileNumberValidator from '@/components/candidate/MobileNumberValidator'

// Data
import countries from '@/data/countries'
import categories from '@/data/disability_categories'

export default {
  components: { MobileNumberValidator },
  props: { profile: { type: Object, required: true }, actionItems: Array },
  data() {
    return {
      countries,
      categories: categories.reduce((acc, i) => { acc[i.value] = i.text; return acc }, {}),
    }
  },
  computed: {
    showWorkRights() {
      return this.profile.workRights && this.profile.workRights[0]
    },
    showLanguages() {
      return this.profile.languages && this.profile.languages[0]
    },
    showPersonalDetails() {
      return true
      return (
        this.profile.countryOfBirth ||
        this.profile.bornAt ||
        this.profile.identification ||
        this.profile.gender
      )
    },
    columns() {
      let count = 0
      if (this.showWorkRights) count++
      if (this.showPersonalDetails) count++
      if (this.showInterests || this.showCompetencies || this.showAffiliations) count++
      return count
    },
  },
  methods: {
    countryName(code) {
      return this.countries.find(country => country.code === code).name
    },
    missing(item) {
      return this.actionItems.some(a => a.type.includes(item))
    },
    copiedToast(value) {
      this.$store.dispatch('snackbarShow', `Copied ${value}.`)
    },
  },
}
</script>
